import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { AppLayout } from '../components/templates/AppLayout/AppLayout';
import { Header } from '../components/layout/Header';
import { Sidebar } from '../components/Sidebar';
import { PageContent } from '../components/templates/PageContent/PageContent';
import { PageHeader } from '../components/templates/PageHeader/PageHeader';
import { Section } from '../components/templates/Section/Section';
import { Alert } from '../components/atoms/Alert/Alert';
import { Button } from '../components/atoms/Button/Button';
import { Card } from '../components/atoms/Card/Card';
import { LiveRegion } from '../components/atoms/LiveRegion/LiveRegion';
import { Stack } from '../components/atoms/Stack/Stack';
import { Heading } from '../components/atoms/Typography/Heading';
import { Text } from '../components/atoms/Typography/Text';
import { LoadErrorState } from '../components/feedback/LoadErrorState/LoadErrorState';
import { ConfirmDialog } from '../components/ui/ConfirmDialog/ConfirmDialog';
import { AiUsageMeter } from '../components/aiUsage/AiUsageMeter';
import { AiLimitErrorMessage, AiLimitNotice } from '../components/aiUsage/AiLimitNotice';
import { MentorMessageList } from '../components/mentor/MentorMessageList';
import { MentorComposer } from '../components/mentor/MentorComposer';
import { MentorConversationList } from '../components/mentor/MentorConversationList';
import { AquariumMemorySection } from '../components/organisms/AquariumMemorySection';
import { useAiUsage } from '../hooks/useAiUsage';
import {
  useAskMentor,
  useDeleteMentorConversation,
  useMentorConversation,
  useMentorConversations,
} from '../hooks/useMentor';
import { getAiLimitErrorKind } from '../services/aiUsageService';
import { MENTOR_STREAM_ABORTED } from '../services/mentorService';
import type { MentorConversationSummary } from '../services/mentorService';
import { useTranslation } from '../i18n/useTranslation';
import './MentorPage.css';

const STARTER_KEYS = ['growth', 'kh', 'icp', 'dosing', 'photos'] as const;

/**
 * KI-Becken-Mentor: an AI chat about the active aquarium. The server builds
 * the tank context (values, ICP, dosing, livestock, journal, tasks, AI
 * findings, newest photos); the page only sends the question and the
 * conversation id. Conversations are kept and can be continued or deleted.
 * The open conversation lives in the URL (?conversation=<id>).
 *
 * Answers stream in (SSE): the question shows at once, the answer grows in
 * place ("Der Mentor schreibt …", aria-busy), Send is disabled and "Cancel"
 * aborts it (nothing is stored). The LiveRegion announces once, when the
 * answer is complete. If the stream fails midway the partial text is
 * discarded (it was not saved and may stop mid-advice) and an Alert offers
 * "Try again" with the same question.
 */
export function MentorPage() {
  const { aquariumId = '' } = useParams<{ aquariumId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();
  const { exceeded, usage } = useAiUsage();

  const activeId = searchParams.get('conversation');
  const setActiveId = (id: string | null): void => {
    const next = new URLSearchParams(searchParams);
    if (id === null) next.delete('conversation');
    else next.set('conversation', id);
    setSearchParams(next, { replace: false });
  };

  // Emergency mode ("Ask the mentor"): a new conversation with a prepared question;
  // the incident goes with every question of it.
  const location = useLocation();
  const fromEmergency = (location.state ?? {}) as { draft?: string; incidentId?: string };
  const [incidentId, setIncidentId] = useState<string | undefined>(fromEmergency.incidentId);
  const [draft, setDraft] = useState(fromEmergency.draft ?? '');
  const [includePhotos, setIncludePhotos] = useState(true);
  const [announcement, setAnnouncement] = useState('');
  const [toDelete, setToDelete] = useState<MentorConversationSummary | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const errorRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const list = useMentorConversations(aquariumId);
  const detail = useMentorConversation(aquariumId, activeId);
  const ask = useAskMentor(aquariumId, (id) => setActiveId(id));
  const remove = useDeleteMentorConversation(aquariumId);

  const send = (content: string): void => {
    const question = content.trim();
    if (question === '' || ask.isPending) return;
    setAnnouncement('');
    ask.mutate(
      { conversationId: activeId, content: question, includePhotos, ...(incidentId ? { incidentId } : {}) },
      {
        onSuccess: ({ reply }) => {
          setDraft('');
          setAnnouncement(t(reply.assistantMessage.truncated === true ? 'mentor.chat.answeredTruncated' : 'mentor.chat.answered'));
          inputRef.current?.focus();
        },
      },
    );
  };

  const errorCode = (ask.error as { code?: string } | null)?.code;
  const cancelled = ask.isError && errorCode === MENTOR_STREAM_ABORTED;

  const cancel = (): void => {
    ask.cancel();
    // The Cancel button disappears: keep focus in the composer.
    inputRef.current?.focus();
  };

  useEffect(() => {
    if (ask.isError && !cancelled) errorRef.current?.focus();
  }, [ask.isError, cancelled]);

  // Keep the newest message in view after an answer (no smooth scroll: reduced motion).
  const messageCount = detail.data?.messages.length ?? 0;
  useEffect(() => {
    if (messageCount > 0 || ask.isPending) messagesEndRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [messageCount, ask.isPending]);

  const startNew = (): void => {
    ask.reset();
    setActiveId(null);
    setIncidentId(undefined);
    setDraft('');
    inputRef.current?.focus();
  };

  const applyStarter = (question: string): void => {
    setDraft(question);
    inputRef.current?.focus();
  };

  const confirmDelete = (): void => {
    if (!toDelete) return;
    const deleted = toDelete;
    remove.mutate(deleted.id, {
      onSuccess: () => {
        setAnnouncement(t('mentor.list.deleted'));
        if (deleted.id === activeId) setActiveId(null);
        setToDelete(null);
      },
      onError: () => setToDelete(null),
    });
  };

  const messages = detail.data?.messages ?? [];
  const pendingQuestion = ask.isPending ? (ask.variables?.content ?? null) : null;
  const showEmpty = messages.length === 0 && pendingQuestion === null && !(activeId !== null && detail.isPending);
  const limitError = ask.isError && getAiLimitErrorKind(ask.error) !== null;
  const conversationTitle = activeId !== null && detail.data?.title ? detail.data.title : t('mentor.chat.newTitle');

  const sendErrorText =
    errorCode === 'RATE_LIMITED' ? t('mentor.errors.rateLimited')
      : errorCode === 'CONVERSATION_FULL' ? t('mentor.errors.conversationFull')
        : ask.receivedText ? t('mentor.errors.interrupted')
          : t('mentor.errors.sendFailed');
  const failedQuestion = ask.isError ? (ask.variables?.content ?? null) : null;
  const canRetry = failedQuestion !== null && errorCode !== 'CONVERSATION_FULL' && errorCode !== 'RATE_LIMITED';

  return (
    <AppLayout
      header={<Header />}
      sidebar={<Sidebar />}
      main={
        <PageContent width="wide" className="mentor-page">
          <PageHeader
            title={t('mentor.title')}
            description={t('mentor.description')}
            actions={
              <Button variant="secondary" onClick={startNew} data-testid="mentor-new">
                {t('mentor.newConversation')}
              </Button>
            }
          />
          <AiUsageMeter variant="compact" framed />

          <div className="mentor-page__layout">
            <Section title={conversationTitle} className="mentor-page__chat" headingProps={{ className: 'mentor-page__chat-title' }}>
              <Card padding="lg">
                <Stack gap="6">
                  {activeId !== null && detail.isError ? (
                    <LoadErrorState
                      compact
                      titleLevel={3}
                      title={t('mentor.errors.conversationLoad')}
                      onRetry={() => void detail.refetch()}
                      retryLabel={t('common.retry')}
                    />
                  ) : activeId !== null && detail.isPending ? (
                    <LiveRegion busy>
                      <Text variant="muted">{t('mentor.chat.loading')}</Text>
                    </LiveRegion>
                  ) : showEmpty ? (
                    <Stack gap="4">
                      {/* One line, not a full empty state: the composer stays near the top (usability audit §5.6). */}
                      <Text variant="muted">{t('mentor.empty.description')}</Text>
                      <Heading level={3} className="mentor-page__starters-title" id="mentor-starters-title">
                        {t('mentor.starters.title')}
                      </Heading>
                      <ul className="mentor-starters" aria-labelledby="mentor-starters-title">
                        {STARTER_KEYS.map((key) => (
                          <li key={key}>
                            <Button
                              variant="secondary"
                              size="sm"
                              className="mentor-starters__chip"
                              onClick={() => applyStarter(t(`mentor.starters.${key}`))}
                            >
                              {t(`mentor.starters.${key}`)}
                            </Button>
                          </li>
                        ))}
                      </ul>
                    </Stack>
                  ) : (
                    <MentorMessageList messages={messages} pendingQuestion={pendingQuestion} streamText={ask.streamText} />
                  )}
                  <div ref={messagesEndRef} />

                  {exceeded && !ask.isError ? (
                    <AiLimitNotice kind="exceeded" resetsAt={usage?.resetsAt} live="off" />
                  ) : null}
                  {cancelled ? (
                    <Alert tone="info" compact data-testid="mentor-cancelled">
                      {t('mentor.chat.cancelled')}
                    </Alert>
                  ) : ask.isError &&
                    (limitError ? (
                      <AiLimitErrorMessage ref={errorRef} error={ask.error} tabIndex={-1} />
                    ) : (
                      <Alert
                        ref={errorRef}
                        tabIndex={-1}
                        compact
                        action={
                          canRetry ? (
                            <Button variant="secondary" size="sm" onClick={() => send(failedQuestion)}>
                              {t('common.retry')}
                            </Button>
                          ) : undefined
                        }
                      >
                        {sendErrorText}
                      </Alert>
                    ))}

                  <MentorComposer
                    ref={inputRef}
                    value={draft}
                    onChange={setDraft}
                    onSubmit={() => send(draft)}
                    includePhotos={includePhotos}
                    onIncludePhotosChange={setIncludePhotos}
                    busy={ask.isPending}
                    onCancel={cancel}
                    disabled={exceeded}
                  />
                  <Stack gap="1">
                    <Text variant="caption">{t('journal.ai.disclaimer')} {t('mentor.disclaimer')}</Text>
                    <Text variant="caption">
                      {t('mentor.dataNotice')}{' '}
                      <Link to="/privacy" className="mentor-page__link">{t('mentor.privacyLink')}</Link>
                    </Text>
                  </Stack>
                </Stack>
              </Card>
            </Section>

            <Section title={t('mentor.list.title')} className="mentor-page__history">
              {list.isError ? (
                <LoadErrorState
                  compact
                  titleLevel={3}
                  title={t('mentor.errors.listLoad')}
                  onRetry={() => void list.refetch()}
                  retryLabel={t('common.retry')}
                />
              ) : list.isPending ? (
                <LiveRegion busy>
                  <Text variant="muted">{t('mentor.list.loading')}</Text>
                </LiveRegion>
              ) : (list.data ?? []).length === 0 ? (
                <Card padding="md">
                  <Text variant="muted">{t('mentor.list.empty')}</Text>
                </Card>
              ) : (
                <Card padding="sm">
                  <MentorConversationList
                    conversations={list.data ?? []}
                    activeId={activeId}
                    onOpen={(id) => {
                      ask.reset();
                      setActiveId(id);
                    }}
                    onDelete={setToDelete}
                  />
                </Card>
              )}
              {remove.isError && <Alert compact>{t('mentor.errors.deleteFailed')}</Alert>}
            </Section>
          </div>

          {/* What the AI has learned about this aquarium — with the AI, not under tasks (usability audit §4.3). */}
          {aquariumId && <AquariumMemorySection aquariumId={aquariumId} />}

          <ConfirmDialog
            isOpen={toDelete !== null}
            title={t('mentor.list.confirmTitle')}
            message={t('mentor.list.confirmMessage', { title: toDelete?.title || t('mentor.list.untitled') })}
            confirmLabel={t('mentor.list.delete')}
            cancelLabel={t('common.cancel')}
            onConfirm={confirmDelete}
            onCancel={() => setToDelete(null)}
            isConfirming={remove.isPending}
          />
          <LiveRegion visuallyHidden>{announcement}</LiveRegion>
        </PageContent>
      }
    />
  );
}

export default MentorPage;
